import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import { BUSINESS, IMG } from "../data/content";
import { scrollToHash } from "../lib/scroll";
import { useOpenStatus } from "../lib/hours";
import { useOrder } from "../context/OrderContext";

const Hero = () => {
  const imgRef = useRef(null);
  const openStatus = useOpenStatus();
  const openOrder = useOrder();
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.06, 1.16]);

  const word = {
    hidden: { opacity: 0, y: "110%" },
    show: (i) => ({
      opacity: 1,
      y: "0%",
      transition: { duration: 0.8, delay: 0.15 + i * 0.09, ease: [0.22, 1, 0.36, 1] },
    }),
  };
  const headline = [
    { t: "Arlington's", gold: false },
    { t: "Favorite", gold: false },
    { t: "Coffee", gold: true },
    { t: "Destination", gold: true },
  ];

  return (
    <section
      id="home"
      className="relative min-h-[100svh] lg:h-[100svh] lg:max-h-[920px] flex flex-col lg:grid lg:grid-cols-[1.05fr_1fr] bg-[#241812]"
      data-testid="hero-section"
    >
      {/* ---------- TEXT PANEL ---------- */}
      <div className="order-2 lg:order-1 relative flex flex-col justify-center px-6 sm:px-10 lg:px-16 py-10 sm:py-14 lg:py-0">
        {/* gold hairline frame */}
        <div className="pointer-events-none absolute inset-4 sm:inset-6 rounded-[1.5rem] border border-white/10" />

        <div className="relative">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="flex items-center gap-3 mb-6"
          >
            <span className="h-px w-8 bg-highlight" />
            <span className="text-[11px] sm:text-xs uppercase tracking-[0.28em] text-highlight font-semibold">
              Arlington, Virginia · Est. 2017
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="text-background text-[11.5vw] sm:text-5xl lg:text-5xl xl:text-6xl leading-[1.03] tracking-tight"
          >
            Arlington's Favorite <span className="text-highlight">Coffee Destination</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-6 max-w-md text-background/80 text-sm sm:text-base leading-relaxed"
          >
            {BUSINESS.subheadline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <button
              onClick={openOrder}
              data-testid="hero-order-online-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-highlight text-foreground px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
            >
              Order Online
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              href="#menu"
              onClick={(e) => { e.preventDefault(); scrollToHash("#menu"); }}
              data-testid="hero-view-menu-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 text-background px-8 py-4 text-sm font-semibold tracking-wide transition-colors duration-300 hover:bg-white/10"
            >
              View Menu
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-background/70"
          >
            <span className="inline-flex items-center gap-1.5 text-sm">
              <Star size={15} className="fill-highlight text-highlight" />
              <span className="text-background font-semibold">{BUSINESS.rating}</span> · 480+ reviews
            </span>
            <span
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium ${
                openStatus.open
                  ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-200"
                  : "border-red-400/40 bg-red-500/15 text-red-200"
              }`}
              data-testid="hero-open-status"
            >
              <span className="relative flex h-2 w-2">
                {openStatus.open && (
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
                )}
                <span
                  className={`relative inline-flex h-2 w-2 rounded-full ${
                    openStatus.open ? "bg-emerald-400" : "bg-red-400"
                  }`}
                />
              </span>
              <span className="font-semibold">{openStatus.statusText}</span>
              <span className="opacity-80">· {openStatus.detail}</span>
            </span>
          </motion.div>
        </div>
      </div>

      {/* ---------- IMAGE PANEL ---------- */}
      <div
        ref={imgRef}
        className="order-1 lg:order-2 relative overflow-hidden min-h-[40svh] lg:min-h-0"
        data-testid="hero-image"
      >
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img
            src={IMG.spread}
            alt="A Detour Coffee Co. brunch spread — signature iced coffee, pastries and Mediterranean plates"
            className="h-full w-full object-cover"
            fetchPriority="high"
          />
        </motion.div>
        {/* blend edges toward the dark panel + legibility scrims */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/25 lg:bg-gradient-to-l lg:from-transparent lg:via-transparent lg:to-[#241812]" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#241812] to-transparent lg:hidden" />
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/45 to-transparent" />

        {/* floating caption chip */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.6 }}
          className="absolute bottom-5 right-5 sm:bottom-7 sm:right-7"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-md border border-white/25 px-4 py-2 text-background text-xs tracking-wide" data-testid="hero-status-chip">
            <span className={`h-1.5 w-1.5 rounded-full ${openStatus.open ? "bg-emerald-400" : "bg-red-400"}`} />
            {openStatus.open ? "Open now · Served fresh, all day" : "Closed · Opens 7:30 AM"}
          </span>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
